import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import EntryCard from "@/components/feature/EntryCard";
import Reveal from "@/components/base/Reveal";
import useSeo from "@/hooks/useSeo";
import { categories } from "@/mocks/categories";
import { getCategoryBySlug, getEntriesByCategory } from "@/utils/content";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = getCategoryBySlug(slug);
  const origin = window.location.origin;

  useSeo({
    title: category
      ? `${category.name}黑料汇总 - 分类查找 - 15黑料网`
      : "分类不存在 - 15黑料网",
    description: category
      ? `浏览「${category.name}」分类下的黑料汇总，共 ${category.count} 条相关词条。${category.desc}支持标签与关键词进一步筛选，快速定位目标内容。`
      : "该分类不存在或已调整，请返回首页重新选择分类。",
    keywords: category
      ? `${category.name},黑料分类,分类查找,黑料标签,15黑料网`
      : "15黑料网,分类查找",
    path: `/category/${slug}`,
    jsonLd: category
      ? [
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name}黑料汇总`,
            url: `${origin}/category/${category.slug}`,
            description: category.desc,
            inLanguage: "zh-CN",
            isPartOf: { "@type": "WebSite", name: "15黑料网", url: origin },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: origin },
              { "@type": "ListItem", position: 2, name: "主题分类", item: `${origin}/keywords` },
              { "@type": "ListItem", position: 3, name: category.name, item: `${origin}/category/${category.slug}` },
            ],
          },
        ]
      : undefined,
  });

  if (!category) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-[1280px] flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-emotion-sad-line text-2xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这个分类</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            分类可能已经调整或重命名。你可以回到首页，从主题分类里重新选一个方向。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const list = getEntriesByCategory(category.slug);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-200/70 bg-background-100/60">
          <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
            <Breadcrumbs
              items={[{ label: "首页", path: "/" }, { label: "主题分类" }, { label: category.name }]}
            />
            <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className={`${category.icon} text-xl`} />
                  </span>
                  {category.name}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {category.desc}
                </p>
              </div>
              <div className="flex gap-6">
                <div>
                  <p className="font-heading text-2xl font-bold text-primary-600">{category.count}</p>
                  <p className="text-xs text-foreground-500">收录词条</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-bold text-accent-600">{list.length}</p>
                  <p className="text-xs text-foreground-500">精选汇总</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
            该分类下的汇总
            <span className="ml-2 text-sm font-normal text-foreground-500">按热度排序</span>
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((entry, index) => (
              <Reveal key={entry.id} delay={index * 50}>
                <EntryCard entry={entry} />
              </Reveal>
            ))}
          </div>

          <div className="mt-12 rounded-lg border border-background-200/70 bg-background-100/60 p-5 md:p-6">
            <h2 className="font-heading text-base font-bold text-foreground-950">换个分类看看</h2>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {categories
                .filter((item) => item.slug !== category.slug)
                .map((item) => (
                  <Link
                    key={item.slug}
                    to={`/category/${item.slug}`}
                    className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-4 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className={`${item.icon} text-base text-primary-600`} />
                    {item.name}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}